import type { AppEnv } from '@shared/config/env'
import type { OSType } from '@shared/models'

import type { LoginCountry } from './login-country'

type NullableValue<T> = T | null | undefined

type LoginSource = 'WEB' | 'APP'

type LoginAvailabilityParams = {
  appEnv: AppEnv
  from: NullableValue<LoginSource>
  os: NullableValue<OSType>
  runtimeVersion: NullableValue<string>
  appleLoginFlagEnabled: boolean
  lineLoginFlagEnabled: boolean
  googleLoginFlagEnabled: boolean
  submittingRuntimeVersion: unknown
  selectedCountry: NullableValue<LoginCountry>
}

const isSocialLoginAlwaysVisibleEnv = (appEnv: AppEnv) =>
  appEnv === 'local' || appEnv === 'development' || appEnv === 'staging' || appEnv === 'qa'

const isAppleDevice = (os: OSType) => os === 'IOS' || os === 'IPAD' || os === 'MACOS'

const isAndroidDevice = (os: OSType) => os === 'ANDROID' || os === 'ANDROID_TABLET'

const isDesktopDevice = (os: OSType) => os === 'DESKTOP'

export const isAppleLoginVisible = ({
  appEnv,
  from,
  os,
  runtimeVersion,
  appleLoginFlagEnabled,
  submittingRuntimeVersion,
  selectedCountry,
}: LoginAvailabilityParams) => {
  if (selectedCountry === 'JP') {
    return false
  }

  if (from === 'WEB' && isSocialLoginAlwaysVisibleEnv(appEnv)) {
    return true
  }

  if (!appleLoginFlagEnabled && !isSocialLoginAlwaysVisibleEnv(appEnv)) {
    return false
  }

  if (from !== 'APP' || !os) {
    return false
  }

  return isAppleDevice(os) && submittingRuntimeVersion === runtimeVersion
}

export const isKakaoLoginVisible = (params: LoginAvailabilityParams) => {
  if (params.selectedCountry === 'JP') {
    return false
  }

  if (params.from === 'WEB' && isSocialLoginAlwaysVisibleEnv(params.appEnv)) {
    return true
  }

  if (!params.os) {
    return true
  }

  if (isDesktopDevice(params.os) || isAndroidDevice(params.os)) {
    return true
  }

  return !isAppleLoginVisible(params)
}

export const isLineLoginVisible = ({ lineLoginFlagEnabled, selectedCountry }: LoginAvailabilityParams) => {
  return lineLoginFlagEnabled && selectedCountry === 'JP'
}

export const isGoogleLoginVisible = ({ googleLoginFlagEnabled, selectedCountry }: LoginAvailabilityParams) => {
  return googleLoginFlagEnabled && selectedCountry !== 'JP'
}
